<script setup lang="ts">
  import CoinsDiscountInfo from '@/entities/Flow/components/FlowPlan/components/CoinsDiscountInfo/CoinsDiscountInfo.vue'
  import { SchemeType, type Plan } from '@/shared/types/api.types'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'
  import { computed, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useQuoteStore, useUserStore } from '@/shared/store'
  import TripIcon from '@/shared/assets/images/trip.png'
  import SinglePlanAccordeon from '../SinglePlanAccordeon/SinglePlanAccordeon.vue'
  import SinglePlanAccordeonItem from '../SinglePlanAccordeonItem/SinglePlanAccordeonItem.vue'
  import CoversPopup from '../CoversPopup/CoversPopup.vue'
  import { useCovers } from '../../composables/useCovers/useCovers'
  import { SINGLE_PLAN_COVERS } from '../../FlowSelectPlanWidget.constants'
  import { useGoogleAnalytics } from '@/shared/composables'
  import type { PlanAccordeonItem } from '../../FlowSelectPlanWidget.types'
  import { toFixedNumber } from '@/shared/utils'
  import { TravelDirection } from '@/shared/types/common.types'

  interface Props {
    plan: Plan
    policyWordingLink: string
  }

  interface Emits {
    (e: 'select-plan', plan: Plan): void
  }

  const props = defineProps<Props>()
  const emit = defineEmits<Emits>()
  const { t } = useI18n()
  const userStore = useUserStore()
  const quoteStore = useQuoteStore()
  const { covers } = useCovers(false, false, false)
  const travelersCount = computed(() => userStore.adultsCount + userStore.childrenCount)

  const hasDiscount = computed(() => props.plan.discount > 0)

  // TODO: Refactor useDiscount and discount logic after Home Banner release
  const discountPercentage = computed(() =>
    Math.floor((props.plan.discount / (props.plan.price + props.plan.discount)) * 100)
  )

  const quotePriceWithoutDiscount = computed(() =>
    toFixedNumber(props.plan.price + props.plan.discount)
  )

  const isWorldwide = computed(() => props.plan.schemeTypeCode === SchemeType.Worldwide)

  const isCoversPopupShown = ref(false)

  const cardData = computed(() => ({
    icon: TripIcon,
    title: 'outbound.select_plan.insurance_values.ww_coverage.header',
    annotation: isWorldwide.value
      ? 'outbound.select_plan.insurance_values.ww_coverage.common_excl'
      : 'outbound.select_plan.insurance_values.ww_coverage.usa_excl',
  }))

  const gaService = useGoogleAnalytics()

  const openPopup = () => {
    isCoversPopupShown.value = true
    gaService.sendClickSelectPlanLearnMore()
  }

  const closePopup = () => {
    isCoversPopupShown.value = false
  }

  const handleToggle = (isOpen: boolean, coverage: PlanAccordeonItem) => {
    if (!isOpen) return

    gaService.sendCoverToggle({
      category: TravelDirection.OUTBOUND,
      planName: props.plan.name,
      planPrice: props.plan.price,
      coverName: t(coverage.title),
      section: 'inclusive_cover',
      numOfTravelers: travelersCount.value,
    })
  }
</script>

<template>
  <div class="single-plan-v2">
    <SinglePlanAccordeon
      :data="SINGLE_PLAN_COVERS"
      class="single-plan-v2__accordion"
      @click-learn-more="openPopup"
      @toggle="handleToggle"
    />
    <div>
      <SinglePlanAccordeonItem :item="cardData" />
    </div>
    <div class="single-plan-v2__content">
      <div class="single-plan-v2__content-header">
        <div class="single-plan-v2__price-block">
          <!-- @vue-ignore -->
          <div
            class="single-plan-v2__price"
            :class="{
              'heading-5': $qicConnect.isMobile,
              'heading-3': !$qicConnect.isMobile,
            }"
          >
            {{ $t('qar_price', { price: plan.price }) }}
          </div>
          <!-- @vue-ignore -->
          <div
            v-if="travelersCount > 1"
            class="single-plan-v2__travelers"
            :class="{
              'text-small-regular': $qicConnect.isMobile,
              'text-regular': !$qicConnect.isMobile,
            }"
          >
            {{
              t(
                'outbound.select_plan.insurance_values.travelers',
                { travelersCount },
                travelersCount
              )
            }}
          </div>
          <div v-if="hasDiscount" class="single-plan-v2__price-discount text-regular">
            {{ $t('qar_price', { price: quotePriceWithoutDiscount }) }}
          </div>
        </div>

        <div class="single-plan-v2__badge-list">
          <div
            v-if="hasDiscount"
            class="single-plan-v2__discount text-small-medium single-plan-v2__badge"
          >
            {{
              t('outbound.select_plan.insurance_values.discount', { discount: discountPercentage })
            }}
          </div>

          <div
            v-if="quoteStore.productBonus"
            class="single-plan-v2__coins text-small-medium single-plan-v2__badge"
          >
            {{ t('select_plan.coins', { value: quoteStore.productBonus }) }}
            <!-- qic-connect-ignore-next-line -->
            <img src="@/shared/assets/images/coin.svg" />
          </div>
        </div>
        <CoinsDiscountInfo
          v-if="plan.priceWithCoinsDiscount"
          :discount-price="Number(plan.priceWithCoinsDiscount)"
          class="single-plan-v2__coins-discount"
        />
      </div>
      <ButtonQC @click="emit('select-plan', plan)">{{
        $t('travel.outbound.select_plan.continue_button')
      }}</ButtonQC>
    </div>
  </div>

  <CoversPopup
    v-if="isCoversPopupShown"
    :policy-wording-link="policyWordingLink"
    :data="covers"
    @close="closePopup"
  />
</template>

<style scoped lang="scss">
  @use '@/shared/assets/styles/utils' as *;

  .single-plan-v2__content {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-6);
    border-top: 1px solid var(--colorBrdrSrfSecondary);
    border-end-start-radius: var(--spacing-8);
    border-end-end-radius: var(--spacing-8);
    padding-block: var(--spacing-6) var(--spacing-8);
    padding-inline: var(--spacing-8);
    margin-inline: calc(var(--spacing-8) * -1);
    margin-top: var(--spacing-8);
    position: sticky;
    bottom: 0;
    background-color: var(--colorBgSrfPrimary);

    /* stylelint-disable-next-line */
    @media (max-width: 768px) {
      gap: var(--spacing-5);
      margin-inline: calc(var(--spacing-5) * -1);
      padding: var(--spacing-5);
      border-end-start-radius: var(--spacing-7);
      border-end-end-radius: var(--spacing-7);
    }
  }

  .single-plan-v2__travelers {
    color: var(--colorTxtSrfSecondary);

    &::before {
      content: '/ ';
    }

    /* stylelint-disable-next-line */
    @media (max-width: 768px) {
      &::before {
        content: none;
      }
    }
  }

  .single-plan-v2__coins {
    background-color: var(--colorBgSrfTertiary);
    color: var(--colorTxtSrfBrandPurple);
  }

  .single-plan-v2__discount {
    background-color: var(--colorBgSrfSuccessGreen);
    color: var(--colorTxtSrfSuccessGreen);
  }

  .single-plan-v2__content-header {
    display: flex;
    justify-content: space-between;
    gap: var(--spacing-1) var(--spacing-2);
    align-items: center;

    &:has(.single-plan-v2__coins-discount) {
      justify-content: center;

      .single-plan-v2__coins-discount {
        flex-basis: 100%;
        display: flex;
        justify-content: center;
      }
    }
  }

  .single-plan-v2__price-block {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--spacing-1) var(--spacing-2);

    .single-plan-v2__price-discount {
      flex-basis: 100%;
    }

    /* stylelint-disable-next-line */
    @media (max-width: 768px) {
      .single-plan-v2__travelers {
        flex-basis: 100%;
        order: 2;
      }

      .single-plan-v2__price-discount {
        flex-basis: auto;
        order: 1;
      }
    }
  }

  .single-plan-v2__badge-list {
    display: flex;
    align-items: center;
    flex-shrink: 0;

    .single-plan-v2__badge {
      display: flex;
      align-items: center;
      gap: var(--spacing-1);
      padding-block: var(--spacing-2);
      padding-inline: var(--spacing-4);
      border: 2px solid var(--colorBgSrfPrimary);
      border-inline-end: none;
      border-radius: var(--spacing-4);

      &:has(+ *) {
        padding-inline-end: var(--spacing-6);
      }

      &:not(:first-child) {
        margin-inline-start: calc(-1 * var(--spacing-4));
      }
    }
  }

  .single-plan-v2__price-discount {
    color: var(--colorTxtSrfSecondary);
    text-decoration: line-through;
  }

  .single-plan-v2__accordion {
    margin-bottom: var(--spacing-5);
  }
</style>
